import {
  TrashIcon,
  PencilSquareIcon,
  ArrowLeftStartOnRectangleIcon,
} from '@heroicons/react/24/solid';
import {
  Position,
  useStore,
  NodeProps,
  NodeToolbar,
  NodeResizer,
  useReactFlow,
  OnResizeStart,
} from '@xyflow/react';
import React, { memo, useState, useCallback } from 'react';
import { Button } from 'src/components/button';
import { useUndoRedo } from 'src/context/undo-redo';
import useDetachNodes from 'src/hooks/useDetachNodes';
import { twMerge } from 'tailwind-merge';

import NoteNodeDialogEdit from './note-node-dialog-edit';
import './note-node.scss';
import { INoteNoteCategory } from './type';

const lineStyle = { borderColor: 'transparent' };

function NoteNode({ id, data, selected, ...rest }: NodeProps) {
  const { deleteElements } = useReactFlow();
  const { setNodes } = useReactFlow();
  const { takeSnapshot } = useUndoRedo();

  const [isShowEdit, setIsShowEdit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const handleDoubleClick = () => {
    setIsEditing(true);
    takeSnapshot();
  };
  const handleBlur = () => {
    setIsEditing(false);
  };
  const handleChange = (e: any) => {
    setNodes((nds) =>
      nds.map((node: any) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                category: {
                  ...node.data?.category,
                  text: e.target.value,
                },
              },
            }
          : node,
      ),
    );
  };

  const onDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const hasParent = useStore((store) => !!store.nodeLookup.get(id)?.parentId);
  const detachNodes = useDetachNodes();

  const onDetach = () => detachNodes([id]);

  const handleSave = (newData: INoteNoteCategory) => {
    takeSnapshot();
    setNodes((nds) =>
      nds.map((node: any) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                category: {
                  ...node.data?.category,
                  ...newData,
                },
              },
            }
          : node,
      ),
    );
  };

  const onResizeStart: OnResizeStart = useCallback(
    (event, node) => {
      takeSnapshot();
    },
    [takeSnapshot],
  );

  return (
    <>
      <NodeResizer
        minWidth={50}
        minHeight={50}
        lineStyle={lineStyle}
        onResizeStart={onResizeStart}
        data-testid="note-node-resizer"
      />
      <div className={twMerge('note-node')}>
        <NodeToolbar
          data-testid="note-node-toolbar"
          className="nodrag"
          position={Position.Bottom}
        >
          <div
            data-testid="note-node-toolbar-buttons"
            className="flex w-full items-center justify-center gap-2"
          >
            <Button
              data-testid="note-node-toolbar-button-edit"
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Edit"
              color="blue"
              onClick={() => setIsShowEdit(true)}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <PencilSquareIcon className="h-4 w-4" />
            </Button>
            <Button
              data-testid="note-node-toolbar-button-delete"
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Delete"
              color="red"
              onClick={onDelete}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
            {hasParent && (
              <Button
                data-testid="note-node-toolbar-button-detach"
                data-tooltip-id="app-tooltip"
                data-tooltip-content="Detach"
                color="orange"
                onClick={() => onDetach()}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ArrowLeftStartOnRectangleIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </NodeToolbar>
        <div
          data-testid="note-node-content"
          className="flex h-full w-full items-center justify-center overflow-hidden bg-yellow-200"
          style={{
            ...((data.category as INoteNoteCategory).color
              ? {
                  backgroundColor: (data.category as INoteNoteCategory).color,
                }
              : {}),
          }}
        >
          {isEditing ? (
            <textarea
              data-testid="note-node-content-textarea"
              className={twMerge([
                'h-full w-full border-none bg-transparent p-2 text-center outline-none',
                (data.category as INoteNoteCategory).bold && 'font-bold',
                (data.category as INoteNoteCategory).italic && 'italic',
                (data.category as INoteNoteCategory).underline && 'underline',
                (data.category as INoteNoteCategory).lineThrough &&
                  'line-through',
              ])}
              ref={(input) => input?.focus()}
              value={
                ((data.category as INoteNoteCategory).text as string) || 'Note'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              style={{
                fontSize: `${(data.category as INoteNoteCategory).fontSize || 16}px`,
              }}
            />
          ) : (
            <div
              data-testid="note-node-content-text"
              className="h-full w-full whitespace-pre-line border-none p-2 text-center"
              onDoubleClick={handleDoubleClick}
              style={{
                cursor: 'pointer',
                fontSize: `${(data.category as INoteNoteCategory).fontSize || 16}px`,
              }}
            >
              {((data.category as INoteNoteCategory).text as string) || 'Note'}
            </div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 h-2 w-full bg-yellow-300 opacity-50" />
      </div>
      <NoteNodeDialogEdit
        id={id}
        labelKey={data.key as string}
        open={isShowEdit}
        onClose={() => setIsShowEdit(false)}
        data={data.category as INoteNoteCategory}
        onSaved={handleSave}
      />
    </>
  );
}

export default memo(NoteNode);
